import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BtnPrimary, BtnSecondary, BtnDestructive } from "@/pages/Senders";
import {
  MicrosoftOutlookLogo, Plus, TestTube, Trash, CheckCircle, Warning,
  Circle, UsersThree, X, MagnifyingGlass, ClipboardText,
} from "@phosphor-icons/react";

/**
 * Microsoft Tenants — pool of Azure AD app registrations (Mail.Send
 * application permission, admin consent) used to send via Microsoft Graph
 * from every mailbox in each tenant.
 *
 * Per-tenant workflow:
 *   1. Add tenant (label + tenant_id + client_id + client_secret)
 *   2. Test — probes login.microsoftonline.com to confirm creds + consent
 *   3. List mailboxes — pulls /users from Graph
 *   4. Enroll → creates senders (assign proxy afterwards in Senders page)
 */
export default function MicrosoftTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [enrollFor, setEnrollFor] = useState(null); // tenant obj
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/microsoft-tenants");
      setTenants(data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tenants");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testOne = async (id) => {
    setTestingId(id);
    try {
      const { data } = await api.post(`/admin/microsoft-tenants/${id}/test`);
      toast[data.ok ? "success" : "error"](data.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTestingId(null); }
  };

  const remove = async (t) => {
    if (t.sender_count > 0) {
      toast.error(`Cannot delete — ${t.sender_count} sender(s) attached`);
      return;
    }
    if (!confirm(`Delete tenant "${t.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/microsoft-tenants/${t.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="microsoft-tenants-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-mono uppercase tracking-wider flex items-center gap-2">
            <MicrosoftOutlookLogo size={22} weight="bold" className="text-[#E1FF00]" />
            Microsoft Tenants
          </h1>
          <p className="text-xs text-[#71717A] mt-1 font-mono max-w-3xl">
            Azure AD app registrations used to send via Microsoft Graph.
            Each tenant needs the <span className="text-[#00E5FF]">Mail.Send</span> application
            permission with tenant admin consent granted.
          </p>
        </div>
        <BtnPrimary onClick={() => setAddOpen(true)} testid="add-tenant-btn">
          <Plus size={13} weight="bold" /> Add tenant
        </BtnPrimary>
      </div>

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="ms-tenants-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Tenant / Client ID</th>
              <th>Senders</th>
              <th>Health</th>
              <th>Last listed</th>
              <th style={{ width: 260 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">[ loading... ]</td></tr>}
            {!loading && tenants.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[#71717A] py-8 font-mono">
                [ no tenants yet — click &quot;Add tenant&quot; ]
              </td></tr>
            )}
            {tenants.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                <td className="font-mono text-xs">{t.label}</td>
                <td className="font-mono text-[10px] text-[#71717A]">
                  <div>t: {t.tenant_id_masked}</div>
                  <div>c: {t.client_id_masked}</div>
                </td>
                <td className="font-mono text-xs">{t.sender_count}</td>
                <td>
                  <HealthPill ok={t.last_test_ok} message={t.last_test_message} testedAt={t.last_tested_at} />
                </td>
                <td className="font-mono text-[10px] text-[#71717A]">
                  {(t.last_listed_at || "").slice(0, 16).replace("T", " ") || "—"}
                  {t.mailbox_count ? ` · ${t.mailbox_count} mailboxes` : ""}
                </td>
                <td>
                  <div className="flex gap-1.5">
                    <BtnSecondary onClick={() => testOne(t.id)} testid={`tenant-test-${t.id}`}>
                      <TestTube size={11} weight="bold" /> {testingId === t.id ? "..." : "Test"}
                    </BtnSecondary>
                    <BtnSecondary onClick={() => setEnrollFor(t)} testid={`tenant-enroll-${t.id}`}>
                      <UsersThree size={11} weight="bold" /> Enroll
                    </BtnSecondary>
                    <BtnDestructive onClick={() => remove(t)} testid={`tenant-delete-${t.id}`} disabled={t.sender_count > 0}>
                      <Trash size={11} weight="bold" />
                    </BtnDestructive>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-[11px] text-[#71717A] font-mono border border-[#2A2A2A] bg-[#0A0A0A] p-4 leading-relaxed">
        <div className="uppercase text-white mb-2">Azure setup checklist</div>
        <div>1. <span className="text-white">Entra ID → App registrations → New registration</span></div>
        <div>2. Copy <span className="text-white">Tenant ID</span> + <span className="text-white">Application (client) ID</span></div>
        <div>3. <span className="text-white">Certificates &amp; secrets → New client secret</span> — copy the <b>value</b> (not the id)</div>
        <div>4. <span className="text-white">API permissions → Microsoft Graph → Application permissions → Mail.Send</span></div>
        <div>5. Click <span className="text-white">Grant admin consent</span> — sending will fail without this</div>
        <div>6. Paste the four values below → Test → List mailboxes → Enroll</div>
      </div>

      {addOpen && <AddTenantModal onClose={() => setAddOpen(false)} onSaved={load} />}
      {enrollFor && <EnrollModal tenant={enrollFor} onClose={() => { setEnrollFor(null); load(); }} />}
    </div>
  );
}


function AddTenantModal({ onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim() || !tenantId.trim() || !clientId.trim() || !clientSecret.trim()) {
      toast.error("All four fields are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/microsoft-tenants", {
        label: label.trim(),
        tenant_id: tenantId.trim(),
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      toast.success("Tenant added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title="Add Microsoft tenant" onClose={onClose} testid="add-tenant-modal">
      <div className="space-y-3">
        <Field label="Label (any name for your reference)" value={label} onChange={setLabel} testid="tenant-label" />
        <Field label="Tenant ID (GUID)" value={tenantId} onChange={setTenantId} mono testid="tenant-id" />
        <Field label="Application (client) ID" value={clientId} onChange={setClientId} mono testid="tenant-client-id" />
        <Field label="Client secret VALUE" value={clientSecret} onChange={setClientSecret} mono type="password" testid="tenant-client-secret" />
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
        <BtnPrimary onClick={save} disabled={saving} testid="tenant-save">
          {saving ? "Saving…" : "Save"}
        </BtnPrimary>
      </div>
    </ModalShell>
  );
}


function EnrollModal({ tenant, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | listing | ready | enrolling
  const [mailboxes, setMailboxes] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [showEnrolled, setShowEnrolled] = useState(true);
  const [tab, setTab] = useState("list"); // list | paste
  const [pasteText, setPasteText] = useState("");
  const cancelledRef = useRef(false);

  const list = async () => {
    setPhase("listing");
    try {
      const { data } = await api.post(`/admin/microsoft-tenants/${tenant.id}/list-mailboxes`, {});
      if (cancelledRef.current) return;
      setMailboxes(data.mailboxes || []);
      setPhase("ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "List mailboxes failed");
      setPhase("idle");
    }
  };
  useEffect(() => {
    list();
    return () => { cancelledRef.current = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return mailboxes.filter((m) => {
      if (!showEnrolled && m.already_enrolled) return false;
      if (!q) return true;
      return (m.email || "").toLowerCase().includes(q)
        || (m.displayName || "").toLowerCase().includes(q);
    });
  }, [mailboxes, search, showEnrolled]);

  const toggle = (email) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(email)) n.delete(email); else n.add(email);
      return n;
    });
  };
  const selectAllVisible = () => {
    setSelected((s) => {
      const n = new Set(s);
      filtered.forEach((m) => n.add(m.email));
      return n;
    });
  };
  const selectNone = () => setSelected(new Set());

  const applyPaste = () => {
    const emails = pasteText.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    if (!emails.length) { toast.error("Paste some emails first"); return; }
    // Match to available mailboxes (case-insensitive)
    const available = new Set(mailboxes.map((m) => m.email.toLowerCase()));
    const matched = emails.filter((e) => available.has(e));
    const missing = emails.filter((e) => !available.has(e));
    setSelected((s) => {
      const n = new Set(s);
      matched.forEach((e) => n.add(e));
      return n;
    });
    if (matched.length) toast.success(`Matched ${matched.length} mailbox(es)`);
    if (missing.length) toast.warning(`${missing.length} email(s) not found in tenant`);
  };

  const enroll = async () => {
    if (selected.size === 0) { toast.error("Select at least one mailbox"); return; }
    setPhase("enrolling");
    try {
      const { data } = await api.post(`/admin/microsoft-tenants/${tenant.id}/enroll`, {
        emails: Array.from(selected),
      });
      toast.success(`Enrolled ${data.created} new · updated ${data.updated}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enroll failed");
      setPhase("ready");
    }
  };

  return (
    <ModalShell title={`Enroll mailboxes — ${tenant.label}`} onClose={onClose} testid="enroll-modal" wide>
      <div className="border-b border-[#2A2A2A] mb-4 flex gap-4 text-[11px] font-mono uppercase">
        <button onClick={() => setTab("list")}
          className={`pb-2 ${tab === "list" ? "text-[#E1FF00] border-b-2 border-[#E1FF00]" : "text-[#71717A]"}`}
          data-testid="tab-list">Browse list</button>
        <button onClick={() => setTab("paste")}
          className={`pb-2 ${tab === "paste" ? "text-[#E1FF00] border-b-2 border-[#E1FF00]" : "text-[#71717A]"}`}
          data-testid="tab-paste">Bulk paste</button>
      </div>

      {phase === "listing" && (
        <div className="text-center py-12 font-mono text-[#71717A]">[ pulling mailboxes from Graph… ]</div>
      )}
      {phase === "idle" && (
        <div className="text-center py-8">
          <BtnPrimary onClick={list}>Retry list mailboxes</BtnPrimary>
        </div>
      )}

      {(phase === "ready" || phase === "enrolling") && tab === "list" && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={13} className="absolute left-3 top-2.5 text-[#71717A]" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or display name…"
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] pl-9 pr-3 py-2 text-xs font-mono focus:border-[#E1FF00] outline-none"
                data-testid="enroll-search"
              />
            </div>
            <label className="text-[11px] font-mono text-[#71717A] flex items-center gap-1.5 select-none">
              <input type="checkbox" checked={showEnrolled} onChange={(e) => setShowEnrolled(e.target.checked)} />
              show already-enrolled
            </label>
            <BtnSecondary onClick={selectAllVisible} testid="enroll-select-all">Select all visible</BtnSecondary>
            <BtnSecondary onClick={selectNone} testid="enroll-select-none">Clear</BtnSecondary>
          </div>
          <div className="border border-[#2A2A2A] max-h-[420px] overflow-y-auto">
            <table className="dtable w-full">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Email</th>
                  <th>Display name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.email}
                    onClick={() => toggle(m.email)}
                    className={`cursor-pointer ${selected.has(m.email) ? "bg-[#1a1a05]" : ""}`}
                    data-testid={`mailbox-row-${m.email}`}
                  >
                    <td><input type="checkbox" readOnly checked={selected.has(m.email)} /></td>
                    <td className="font-mono text-xs">{m.email}</td>
                    <td className="font-mono text-xs text-[#A1A1AA]">{m.displayName || "—"}</td>
                    <td className="text-[10px] font-mono">
                      {m.already_enrolled && <span className="text-[#00E5FF]">enrolled</span>}
                      {!m.accountEnabled && <span className="text-[#FF3B30] ml-2">disabled</span>}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-[#71717A] py-8 font-mono">[ no matches ]</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {phase === "ready" && tab === "paste" && (
        <div className="space-y-3">
          <div className="text-[11px] font-mono text-[#71717A]">
            Paste emails (newline, comma or space-separated). Any that match a mailbox in this tenant will be added to your selection.
          </div>
          <textarea
            value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder="alice@contoso.com&#10;bob@contoso.com&#10;carol@contoso.com"
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] p-3 text-xs font-mono focus:border-[#E1FF00] outline-none"
            data-testid="enroll-paste-textarea"
          />
          <div className="flex justify-end">
            <BtnPrimary onClick={applyPaste} testid="enroll-paste-apply">
              <ClipboardText size={12} weight="bold" /> Match to selection
            </BtnPrimary>
          </div>
        </div>
      )}

      {(phase === "ready" || phase === "enrolling") && (
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#2A2A2A]">
          <div className="text-[11px] font-mono text-[#71717A]">
            Selected: <span className="text-[#E1FF00]">{selected.size}</span> · Total mailboxes: {mailboxes.length}
          </div>
          <div className="flex gap-2">
            <BtnSecondary onClick={onClose}>Close</BtnSecondary>
            <BtnPrimary onClick={enroll} disabled={selected.size === 0 || phase === "enrolling"} testid="enroll-submit">
              {phase === "enrolling" ? "Enrolling…" : `Enroll ${selected.size}`}
            </BtnPrimary>
          </div>
        </div>
      )}
    </ModalShell>
  );
}


function ModalShell({ title, onClose, children, testid, wide = false }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-[#0A0A0A] border border-[#2A2A2A] p-6 max-h-[92vh] overflow-y-auto ${wide ? "w-full max-w-4xl" : "w-full max-w-lg"}`}
        data-testid={testid}
      >
        <div className="flex items-center justify-between mb-5">
          <div className="font-mono uppercase text-sm tracking-wider">{title}</div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white">
            <X size={16} weight="bold" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


function Field({ label, value, onChange, mono = false, type = "text", testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] font-mono mb-1">{label}</div>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-[#050505] border border-[#2A2A2A] px-3 py-2 text-xs focus:border-[#E1FF00] outline-none ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}


function HealthPill({ ok, message, testedAt }) {
  if (ok === true) return (
    <span title={message || ""} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#00E5FF]">
      <CheckCircle size={11} weight="fill" /> ok
    </span>
  );
  if (ok === false) return (
    <span title={message || ""} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#FF3B30]">
      <Warning size={11} weight="fill" /> broken
    </span>
  );
  return (
    <span title={testedAt || "not tested"} className="inline-flex items-center gap-1 text-[11px] font-mono text-[#71717A]">
      <Circle size={9} weight="fill" /> untested
    </span>
  );
}
